import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import {
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Snackbar,
  Alert,
  LinearProgress,
  Chip,
  StepIconProps,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import PublishedWithChangesRoundedIcon from "@mui/icons-material/PublishedWithChangesRounded";
import UnpublishedRoundedIcon from "@mui/icons-material/UnpublishedRounded";
import { useParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import IProduct from "../../entities/IProduct";
import ICategory from "../../entities/ICategory";
import IUser from "../../entities/IUser";
import IShop from "../../entities/IShop";
import CategoryPickerStep from "./CategoryPickerStep";
import BasicDetailsStep from "./BasicDetailsStep";
import PricingStep from "./PricingStep";
import InventoryStep from "./InventoryStep";
import { ProductImagesProgressBarForm } from "./ProductImagesProgressBarForm";
import ProductVariantsForm from "./ProductVariantsForm";
import { useProductSubmitData } from "./useProductSubmitData";
import { useProductData } from "./useProductData";
import { isVariableProductType, extractCategoryId } from "../../services/productTypeUtils";
import { PRODUCT_STATUS, isProductPublished } from "../../services/productStatus";
import ProductService from "../../services/ProductService";
import CategoryService from "../../services/CategoryService";
import { unwrapList, unwrapData } from "../../services/apiUtils";
import productEvents from "../../services/productEvents";
import ShopService from "../../services/ShopService";
import UserService from "../../services/UserService";
import AuthContext from "../../store/auth-context";
import { toPlainText, textToHtml, msToIso, promoDateToMs } from "../../services/textUtils";
import { theme } from "../../theme";

const validationSchema = yup.object({
  customerId: yup
    .number()
    .required("Please select a user")
    .moreThan(0, "Please select a user"),
  shopId: yup
    .number()
    .required("Please select a shop")
    .moreThan(0, "Please select a shop"),
  title: yup.string().required("Title is required"),
  description: yup.string().required("Highlights are required"),
  categoryId: yup
    .number()
    .required("Category is required")
    .moreThan(0, "Category is required"),
  price: yup
    .number()
    .required("Price is required")
    .moreThan(0, "Price must be greater than 0"),
  stock: yup
    .number()
    .min(0, "Stock cannot be negative")
    .required("Stock is required"),
  brand: yup.string().required("Brand is required"),
  deliveryCharges: yup
    .number()
    .min(0, "Delivery charges cannot be negative")
    .required("Delivery charges are required"),
  deliveryDays: yup
    .number()
    .min(0, "Delivery days cannot be negative")
    .required("Delivery days are required"),
});

const STEP_FIELDS: Record<number, Array<keyof IProduct>> = {
  0: ["customerId", "shopId", "title", "description", "categoryId", "brand"],
  1: [],
  2: ["price"],
  3: ["stock", "deliveryCharges", "deliveryDays"],
};

const buildInitialValues = (): IProduct => ({
  productId: 0,
  title: "",
  description: "",
  vendorId: 0,
  price: 0,
  old_price: 0,
  product_detail: "",
  product_type: "single",
  weight: 0,
  height: 0,
  size: 0,
  special_price: 0,
  country_of_manufacture: "",

  productTax: 0,
  productCost: 0,
  productOtherTax: 0,
  deliveryCost: 0,
  deliveryTax: 0,
  deliveryCharges: 0,
  deliveryDays: 0,
  deliveryMethod: 0,
  service_fee_in_percent: 0,
  service_fee_in_val: 0,
  service_fee: 0,
  otherCharges: 0,

  stock: 0,
  brand: "",
  thumbnail: "",
  metaData: "",
  categoryId: 0,
  brandMake: null,
  categoryName: "",
  estatusId: PRODUCT_STATUS.DRAFT,
  discount_in_percent: 0,
  discount_in_value: 0,
  discounted_price: 0,
  shopId: 0,
  customerId: 0,
  promoStartDate: null,
  promoEndDate: null,
  mediaList: [],
});

const StepIcon = (props: StepIconProps) => {
  const { active, completed, icon } = props;
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        fontWeight: 700,
        color: completed || active ? "#fff" : theme.palette.greyColors?.main || "#999",
        bgcolor: completed ? theme.palette.primary.main : active ? theme.palette.primary.main : "#e4e4e4",
      }}
    >
      {completed ? <CheckRoundedIcon sx={{ fontSize: 16 }} /> : icon}
    </Box>
  );
};

const ProductWizard = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const authCtx = React.useContext(AuthContext);
  const isEditMode = Number(id) > 0;

  const [activeStep, setActiveStep] = useState(0);
  const [maxStepReached, setMaxStepReached] = useState(0);
  const [productId, setProductId] = useState<number>(Number(id) || 0);
  const [publishing, setPublishing] = useState(false);
  const [categoryData, setCategoryData] = useState<Array<ICategory>>([]);
  const [categoryChosen, setCategoryChosen] = useState(isEditMode);

  const [customers, setCustomers] = useState<Array<IUser>>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [shops, setShops] = useState<Array<IShop>>([]);
  const [loadingShops, setLoadingShops] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  useEffect(() => {
    CategoryService.getCategory()
      .then((res: any) => setCategoryData(unwrapList<ICategory>(res)))
      .catch(() => setCategoryData([]));
  }, []);

  useEffect(() => {
    setLoadingCustomers(true);
    UserService.getUsers()
      .then((res: any) => setCustomers(unwrapList<IUser>(res)))
      .catch(() => setCustomers([]))
      .finally(() => setLoadingCustomers(false));
  }, []);

  const loadShopsForCustomer = useCallback((customerId: number) => {
    if (!customerId) {
      setShops([]);
      return;
    }
    setLoadingShops(true);
    ShopService.getShopsByCustomerId(customerId)
      .then((res: any) => setShops(unwrapList<IShop>(res)))
      .catch(() => setShops([]))
      .finally(() => setLoadingShops(false));
  }, []);




  useEffect(() => {
    if (isEditMode) return;
    const customerId = authCtx.user?.userId;
    if (!customerId) return;
    formik.setFieldValue("customerId", customerId);
    loadShopsForCustomer(customerId);
    ShopService.getShopsByCustomerId(customerId)
      .then((res: any) => {
        const shopsList = unwrapList<IShop>(res);
        if (shopsList.length > 0) {
          formik.setFieldValue("shopId", shopsList[0].shopId);
        }
      })
      .catch(() => {});

  }, [authCtx.user?.userId, isEditMode]);

  const handleCustomerChange = (customerId: number) => {
    loadShopsForCustomer(customerId);
  };

  const formik = useFormik<IProduct>({
    initialValues: buildInitialValues(),
    validationSchema,
    onSubmit: () => {

    },
  });

  const handleDefaultValues = useCallback((product: IProduct) => {
    if (!product) return;
    formik.setValues({
      ...formik.values,
      ...product,



      description: toPlainText(product.description),
      categoryId: extractCategoryId(product),



      promoStartDate: promoDateToMs(product.promoStartDate),
      promoEndDate: promoDateToMs(product.promoEndDate),
    });
    setMaxStepReached(3);
    setCategoryChosen(true);

    if (product.customerId) {
      loadShopsForCustomer(product.customerId);
    } else if (product.shopId) {


      ShopService.getShopById(product.shopId)
        .then((res: any) => {
          const shop = unwrapData(res) as IShop;
          const resolvedCustomerId = shop?.customerId;
          if (resolvedCustomerId) {
            formik.setFieldValue("customerId", resolvedCustomerId);
            loadShopsForCustomer(resolvedCustomerId);
          }
        })
        .catch(() => {});
    }

  }, []);

  const { handleSubmit: submitProduct } = useProductSubmitData(buildInitialValues());
  const { isLoading } = useProductData(productId, handleDefaultValues);

  const isPublished = isProductPublished(formik.values);

  const saveCurrentValues = (extra?: Partial<IProduct>) => {
    const values = { ...formik.values, ...extra };
    if (!values.customerId || !values.shopId) {
      setSnackbar({
        open: true,
        message: "Please select a user and a shop for this product first.",
        severity: "error",
      });
      return Promise.reject(new Error("no-shop"));
    }

    const payload: IProduct = {
      ...values,



      description: textToHtml(values.description),


      promoStartDate: msToIso(values.promoStartDate),
      promoEndDate: msToIso(values.promoEndDate),
      shopId: values.shopId,
      customerId: values.customerId,
      productId,
    };

    const isCreating = !productId || productId <= 0;
    const payloadForApi: any = { ...payload };
    if (isCreating) {
      delete payloadForApi.productId;
    }

    return submitProduct(payloadForApi).then((res: any) => {
      const savedId = res?.data?.productId || res?.data?.data?.productId || productId;
      if (savedId && savedId !== productId) {
        setProductId(savedId);
        productEvents.emitProductChanged(savedId);
        navigate(`/products/add-product/${savedId}`, { replace: true });
      } else if (savedId) {
        productEvents.emitProductChanged(savedId);
      }
      formik.setValues({ ...values, productId: savedId || values.productId });
      return savedId;
    });
  };






  const applyCategoryVariantType = useCallback((categoryId: number) => {
    if (!categoryId) return;
    CategoryService.getCategoryVariants(categoryId)
      .then((variants) => {
        const hasVariants = (variants || []).length > 0;
        formik.setFieldValue("product_type", hasVariants ? "variable" : "single");
      })
      .catch(() => {
        formik.setFieldValue("product_type", "single");
      });

  }, []);

  const handleCategoryPicked = (categoryId: number, categoryName: string) => {
    formik.setFieldValue("categoryId", categoryId);
    formik.setFieldValue("categoryName", categoryName);
    applyCategoryVariantType(categoryId);
    setCategoryChosen(true);
  };

  const handleCategoryChangeInStep = (categoryId: number) => {
    const cat = categoryData.find((c: any) => c.categoryId === categoryId);
    formik.setFieldValue("categoryId", categoryId);
    formik.setFieldValue("categoryName", cat?.title || "");
    applyCategoryVariantType(categoryId);
  };

  const validateStep = async (step: number): Promise<boolean> => {
    const fields = STEP_FIELDS[step] || [];
    if (fields.length === 0) return true;
    const errors = await formik.validateForm();
    const touchedPatch: Record<string, boolean> = {};
    fields.forEach((f) => (touchedPatch[f as string] = true));
    formik.setTouched({ ...formik.touched, ...touchedPatch });
    return fields.every((f) => !errors[f]);
  };

  const handleNext = async (step: number) => {
    const valid = await validateStep(step);
    if (!valid) {
      setSnackbar({ open: true, message: "Please fill in the required fields.", severity: "error" });
      return;
    }
    try {
      await saveCurrentValues();
      setActiveStep(step + 1);
      setMaxStepReached((m) => Math.max(m, step + 1));
    } catch {
      setSnackbar({
        open: true,
        message: "Could not save the product. Please check the fields and try again.",
        severity: "error",
      });
    }
  };

  const handleFinish = async () => {
    const valid = await validateStep(3);
    if (!valid) {
      setSnackbar({ open: true, message: "Please fill in the required fields.", severity: "error" });
      return;
    }
    try {
      await saveCurrentValues();
      setSnackbar({ open: true, message: "Product saved successfully.", severity: "success" });
    } catch {
      setSnackbar({
        open: true,
        message: "Could not save the product. Please check the fields and try again.",
        severity: "error",
      });
    }
  };

  const handleTogglePublish = () => {
    if (!productId) return;
    const nextStatus = isPublished ? PRODUCT_STATUS.DRAFT : PRODUCT_STATUS.PUBLISHED;
    setPublishing(true);
    ProductService.updateProduct(productId, {
      ...formik.values,
      description: textToHtml(formik.values.description),
      promoStartDate: msToIso(formik.values.promoStartDate),
      promoEndDate: msToIso(formik.values.promoEndDate),
      estatusId: nextStatus,
    })
      .then(() => {
        formik.setFieldValue("estatusId", nextStatus);
        setSnackbar({
          open: true,
          message:
            nextStatus === PRODUCT_STATUS.PUBLISHED
              ? "Product published. It is now locked from editing — unpublish it first if you need to change it."
              : "Product unpublished. You can now edit it.",
          severity: "success",
        });
        productEvents.emitProductChanged(productId);
      })
      .catch((err: any) => {
        setSnackbar({
          open: true,
          message:
            err?.friendlyMessage ||
            err?.response?.data?.message ||
            "Could not update the publish status. Please try again.",
          severity: "error",
        });
      })
      .finally(() => setPublishing(false));
  };

  if (!categoryChosen) {
    return (
      <CategoryPickerStep
        categoryData={categoryData}
        customers={customers}
        loadingCustomers={loadingCustomers}
        shops={shops}
        loadingShops={loadingShops}
        customerId={formik.values.customerId}
        shopId={formik.values.shopId}
        onCustomerChange={(customerId) => {
          formik.setFieldValue("customerId", customerId);
          formik.setFieldValue("shopId", 0);
          handleCustomerChange(customerId);
        }}
        onShopChange={(shopId) => formik.setFieldValue("shopId", shopId)}
        onNext={handleCategoryPicked}
      />
    );
  }

  const steps = [
    { label: "Basic Details" },
    { label: "Media & Images" },
    { label: "Pricing" },
    { label: "Inventory" },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      {productId > 0 && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
          mb={2}
          sx={{
            bgcolor: isPublished ? "#eafaf1" : "#fff8e6",
            border: `1px solid ${isPublished ? "#34c38f" : "#f1b44c"}`,
            borderRadius: "12px",
            px: 2,
            py: 1.25,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.25}>
            <Chip
              size="small"
              color={isPublished ? "success" : "warning"}
              label={isPublished ? "Published" : "Draft"}
              icon={isPublished ? <PublishedWithChangesRoundedIcon /> : <UnpublishedRoundedIcon />}
            />
            <span>
              {isPublished
                ? "This product is live and locked from editing. Unpublish it first to make changes."
                : "This product is a draft and is not visible to customers yet."}
            </span>
          </Box>
          <Button
            size="small"
            variant={isPublished ? "outlined" : "contained"}
            color={isPublished ? "error" : "success"}
            disabled={publishing}
            onClick={handleTogglePublish}
            startIcon={isPublished ? <UnpublishedRoundedIcon /> : <PublishedWithChangesRoundedIcon />}
          >
            {publishing ? "Please wait..." : isPublished ? "Unpublish" : "Publish"}
          </Button>
        </Box>
      )}

      <Box
        sx={{
          bgcolor: "white",
          borderRadius: "12px",
          boxShadow:
            "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
          p: { xs: 2, sm: 3 },
        }}
      >
        {isLoading && <LinearProgress color="secondary" sx={{ mb: 2 }} />}

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => {
            const disabled = index > maxStepReached;
            return (
              <Step key={step.label} completed={index < activeStep || (isEditMode && index < maxStepReached)}>
                <StepLabel
                  StepIconComponent={StepIcon}
                  onClick={() => !disabled && setActiveStep(index)}
                  sx={{ cursor: disabled ? "default" : "pointer" }}
                >
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {step.label}
                  </Box>
                </StepLabel>
                <StepContent>
                  <Box sx={{ py: 1 }}>
                    {index === 0 && (
                      <>
                        <BasicDetailsStep
                          formik={formik}
                          categoryData={categoryData}
                          disabled={isPublished}
                          onCategoryChange={handleCategoryChangeInStep}
                          customers={customers}
                          loadingCustomers={loadingCustomers}
                          shops={shops}
                          loadingShops={loadingShops}
                          onCustomerChange={handleCustomerChange}
                        />
                        <Button
                          variant="contained"
                          disabled={isPublished}
                          onClick={() => handleNext(0)}
                          sx={{ mt: 1, borderRadius: "10px", px: 3 }}
                        >
                          Next Step
                        </Button>
                      </>
                    )}

                    {index === 1 && (
                      <>
                        {productId > 0 ? (
                          <ProductImagesProgressBarForm
                            {...formik.values}
                            onThumbnailSaved={(url) => formik.setFieldValue("thumbnail", url)}
                            onGalleryChange={(mediaList) => formik.setFieldValue("mediaList", mediaList)}
                          />
                        ) : (
                          <Box sx={{ color: "text.secondary", mb: 2 }}>
                            Save the basic details first to unlock media upload.
                          </Box>
                        )}
                        <Box display="flex" gap={1.5}>
                          <Button variant="outlined" onClick={() => setActiveStep(0)} sx={{ borderRadius: "10px" }}>
                            Back
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => handleNext(1)}
                            sx={{ borderRadius: "10px", px: 3 }}
                          >
                            Next Step
                          </Button>
                        </Box>
                      </>
                    )}

                    {index === 2 && (
                      <>
                        <PricingStep formik={formik} disabled={isPublished}>
                          {isVariableProductType(formik.values.product_type) && productId > 0 && (
                            <ProductVariantsForm
                              productId={productId}
                              categoryId={Number(formik.values.categoryId) || 0}
                              mediaList={formik.values.mediaList}
                              disabled={isPublished}
                              onSaved={() => productEvents.emitProductChanged(productId)}
                            />
                          )}
                        </PricingStep>
                        <Box display="flex" gap={1.5} sx={{ mt: 1 }}>
                          <Button variant="outlined" onClick={() => setActiveStep(1)} sx={{ borderRadius: "10px" }}>
                            Back
                          </Button>
                          <Button
                            variant="contained"
                            disabled={isPublished}
                            onClick={() => handleNext(2)}
                            sx={{ borderRadius: "10px", px: 3 }}
                          >
                            Next Step
                          </Button>
                        </Box>
                      </>
                    )}

                    {index === 3 && (
                      <>
                        <InventoryStep formik={formik} disabled={isPublished} />
                        <Box display="flex" gap={1.5} sx={{ mt: 1 }}>
                          <Button variant="outlined" onClick={() => setActiveStep(2)} sx={{ borderRadius: "10px" }}>
                            Back
                          </Button>
                          <Button
                            variant="contained"
                            disabled={isPublished}
                            onClick={handleFinish}
                            sx={{ borderRadius: "10px", px: 3, boxShadow: "0px 4px 10px rgba(246,130,31,0.3)" }}
                          >
                            Save Product
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                </StepContent>
              </Step>
            );
          })}
        </Stepper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductWizard;
